// Build the public webapp URL for an event under the event-type routing scheme
// (`/{slug}/{eventId}`). This is the single source of truth for how the stored
// `events.webappUrl` field and every admin-side QR code are constructed.

import { DEFAULT_EVENT_TYPES, DEFAULT_TYPE } from '../constants/eventTypes'

// The public app base baked into the stored URL. Always the production/public
// URL (VITE_PUBLIC_APP_URL) — never the dev-localhost override, because the
// value is persisted in Firestore and read by other apps (e.g. the desktop QR).
export const PUBLIC_APP_BASE =
  import.meta.env.VITE_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '')

// Resolve a type's URL slug. Prefer a live type-config object (from
// EventTypesContext); fall back to the bundled defaults, then the type id.
export function slugForType(typeConfig, typeId) {
  return (
    typeConfig?.urlSlug ||
    DEFAULT_EVENT_TYPES[typeId]?.urlSlug ||
    typeId ||
    DEFAULT_TYPE
  )
}

// Server copy: admin/functions/lib/events.js buildWebappUrl (with PUBLIC_APP_BASE
// hardcoded there). Change one, change the other.
// Compose an absolute webapp URL: `${appBase}/${slug}/${eventId}`.
export function buildWebappUrl(appBase, slug, eventId) {
  return new URL(`/${slug}/${eventId}`, appBase).toString()
}

// The stored-value builder: always uses PUBLIC_APP_BASE.
export function storedWebappUrl(slug, eventId) {
  return buildWebappUrl(PUBLIC_APP_BASE, slug, eventId)
}

// The webapp dev-server origin, used only when the admin itself runs in dev
// mode so QR codes point at the locally-running webapp instead of production.
export const DEV_APP_BASE =
  typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:5173`
    : ''

// The URL to *display* in a QR code / copy field. In dev, always points at the
// local webapp using the new /{slug}/{eventId} scheme. In production, prefers
// the event's stored webappUrl, falling back to composing it from the type slug
// (for events created before the field was backfilled).
export function displayWebappUrl({ storedUrl, typeConfig, eventType, eventId }) {
  const slug = slugForType(typeConfig, eventType)
  if (import.meta.env.DEV) {
    return buildWebappUrl(DEV_APP_BASE, slug, eventId)
  }
  return storedUrl || buildWebappUrl(PUBLIC_APP_BASE, slug, eventId)
}

// The public gallery URL for an event: `${appBase}/gallery/${eventId}`. Unlike
// the recording URL it carries no type slug — the gallery route is flat. Points
// at the local webapp in dev, the production base otherwise, mirroring
// displayWebappUrl.
export function galleryUrl(eventId) {
  const base = import.meta.env.DEV ? DEV_APP_BASE : PUBLIC_APP_BASE
  return new URL(`/gallery/${eventId}`, base).toString()
}
